import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const SettingsEditor = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [currentSetting, setCurrentSetting] = useState({
    id: null,
    key: '',
    value: '',
    setting_group: 'general',
    translations: {
      en: { label: '', setting_value: '' },
      cs: { label: '', setting_value: '' },
    },
    file: null, // Pouze pro nastavení s médiem (např. hero_video_path, hero_image)
  });
  const language = getUserLanguage();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'accept-language': language };
      const data = await get('/settings', headers);
      setSettings(data || []);
    } catch (err) {
      console.error('[SettingsEditor] Error fetching settings:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst nastavení' : 'Failed to load settings');
      setSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang = language) => {
    const { name, value, type, checked, files } = e.target;
    
    if (name === 'label') {
      // Upravujeme popisek pro daný jazyk
      setCurrentSetting((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            label: value,
          },
        },
      }));
    } else if (name === 'setting_value') {
      // Upravujeme hodnotu pro daný jazyk
      setCurrentSetting((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            setting_value: value,
          },
        },
      }));
    } else if (name === 'file') {
      const file = files[0];
      setSelectedFile(file);
      setCurrentSetting((prev) => ({
        ...prev,
        file: file,
      }));
    } else {
      setCurrentSetting((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const resetForm = () => {
    setCurrentSetting({
      id: null,
      key: '',
      value: '',
      setting_group: 'general',
      translations: {
        en: { label: '', setting_value: '' },
        cs: { label: '', setting_value: '' },
      },
      file: null,
    });
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Kontrola povinných polí
    if (!currentSetting.key.trim()) {
      setError(language === 'cs' ? 'Klíč je povinný' : 'Key is required');
      return;
    }

    // Pro běžná nastavení pracujeme s JSON payload
    const isMediaSetting =
      currentSetting.key === 'hero_video_path' || currentSetting.key === 'hero_image';
    let payload;
    
    if (isMediaSetting) {
      payload = new FormData();
      payload.append('setting_key', currentSetting.key);
      payload.append('setting_group', currentSetting.setting_group);
      if (currentSetting.file) {
        payload.append('file', currentSetting.file);
      } else {
        payload.append('value', currentSetting.value || '');
      }
      
      // Only include setting_value in translations
      payload.append(
        'translations',
        JSON.stringify({
          en: { setting_value: currentSetting.translations.en.setting_value || '' },
          cs: { setting_value: currentSetting.translations.cs.setting_value || '' }
        })
      );
    } else {
      payload = {
        setting_key: currentSetting.key,
        setting_group: currentSetting.setting_group || 'general',
        value: currentSetting.value || '',
        translations: {
          en: { setting_value: currentSetting.translations.en.setting_value || '' },
          cs: { setting_value: currentSetting.translations.cs.setting_value || '' }
        }
      };
    }

    try {
      if (editMode) {
        await put(`/settings/${currentSetting.id}`, payload);
        setSuccess(language === 'cs' ? 'Nastavení úspěšně aktualizováno' : 'Setting updated successfully');
      } else {
        await post('/settings', payload);
        setSuccess(language === 'cs' ? 'Nastavení úspěšně přidáno' : 'Setting added successfully');
      }
      resetForm();
      fetchSettings();
    } catch (err) {
      console.error('[SettingsEditor] Error saving setting:', err);
      setError(language === 'cs' ? `Nepodařilo se uložit nastavení: ${err.message}` : `Failed to save setting: ${err.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      const setting = await get(`/settings/${id}`);
      setCurrentSetting({
        id: setting.id,
        key: setting.setting_key || '',
        value: setting.value || '',
        setting_group: setting.setting_group || 'general',
        translations: setting.translations || {
          en: { label: '', setting_value: '' },
          cs: { label: '', setting_value: '' },
        },
        file: null,
      });
      setEditMode(true);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[SettingsEditor] Error fetching setting for edit:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst nastavení pro úpravu' : 'Failed to load setting for editing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat toto nastavení?' : 'Are you sure you want to delete this setting?')) {
      return;
    }
    try {
      await del(`/settings/${id}`);
      setSuccess(language === 'cs' ? 'Nastavení úspěšně smazáno' : 'Setting deleted successfully');
      fetchSettings();
    } catch (err) {
      console.error('[SettingsEditor] Error deleting setting:', err);
      setError(language === 'cs' ? 'Nepodařilo se smazat nastavení' : 'Failed to delete setting');
    }
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">
          {language === 'cs' ? 'Správa nastavení' : 'Settings Management'}
        </h1>
        {!showForm && (
          <button
            onClick={handleAddNew}
            className="admin-btn admin-btn-primary"
          >
            {language === 'cs' ? 'Přidat nastavení' : 'Add Setting'}
          </button>
        )}
      </div>

      {error && (
        <div className="admin-alert admin-alert-error">{error}</div>
      )}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode
                ? language === 'cs'
                  ? 'Upravit nastavení'
                  : 'Edit Setting'
                : language === 'cs'
                ? 'Přidat nastavení'
                : 'Add Setting'}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Label</label>
                    <input
                      type="text"
                      name="label"
                      className="admin-form-input"
                      value={currentSetting.translations.en.label || ''}
                      onChange={(e) => handleChange(e, 'en')}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Value</label>
                    <input
                      type="text"
                      name="setting_value"
                      className="admin-form-input"
                      value={currentSetting.translations.en.setting_value || ''}
                      onChange={(e) => handleChange(e, 'en')}
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Popisek</label>
                    <input
                      type="text"
                      name="label"
                      className="admin-form-input"
                      value={currentSetting.translations.cs.label || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Hodnota</label>
                    <input
                      type="text"
                      name="setting_value"
                      className="admin-form-input"
                      value={currentSetting.translations.cs.setting_value || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                    />
                  </div>
                </div>

                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">
                      {language === 'cs' ? 'Klíč' : 'Key'}
                    </label>
                    <input
                      type="text"
                      name="key"
                      className="admin-form-input"
                      value={currentSetting.key || ''}
                      onChange={handleChange}
                      disabled={editMode} // Key nelze měnit při úpravě
                      required
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">
                      {language === 'cs' ? 'Skupina' : 'Group'}
                    </label>
                    <select
                      name="setting_group"
                      className="admin-form-select"
                      value={currentSetting.setting_group || 'general'}
                      onChange={handleChange}
                    >
                      <option value="general">General</option>
                      <option value="seo">SEO</option>
                      <option value="social">Social</option>
                      <option value="contact">Contact</option>
                      <option value="appearance">Appearance</option>
                    </select>
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">
                      {language === 'cs' ? 'Hodnota (společná)' : 'Value (common)'}
                    </label>
                    <input
                      type="text"
                      name="value"
                      className="admin-form-input"
                      value={currentSetting.value || ''}
                      onChange={handleChange}
                    />
                  </div>

                  {(currentSetting.key === 'hero_video_path' ||
                    currentSetting.key === 'hero_image') && (
                    <div className="admin-mb-4">
                      <label className="admin-form-label">
                        {language === 'cs' ? 'Obrázek / Video' : 'Image / Video'}
                      </label>
                      <input
                        type="file"
                        name="file"
                        className="admin-form-input"
                        onChange={handleChange}
                      />
                      {currentSetting.value && (
                        <div className="admin-mt-2">
                          <p className="admin-text-sm admin-text-gray-300">
                            {language === 'cs' ? 'Aktuální soubor:' : 'Current file:'} {currentSetting.value}
                          </p>
                          {(currentSetting.value.endsWith('.jpg') || 
                            currentSetting.value.endsWith('.jpeg') || 
                            currentSetting.value.endsWith('.png') || 
                            currentSetting.value.endsWith('.gif')) && (
                            <img
                              src={`${IMAGE_BASE_URL}${currentSetting.value}`}
                              alt="Preview"
                              className="admin-mt-2 admin-h-32 admin-object-contain admin-rounded"
                              onError={(e) => {
                                console.error(`Failed to load image: ${currentSetting.value}`);
                                e.target.onerror = null;
                                e.target.src = '/images/placeholder.png';
                              }}
                            />
                          )}
                        </div>
                      )}
                      {selectedFile && (
                        <div className="admin-mt-2">
                          <p className="admin-text-sm admin-text-gray-300">
                            {language === 'cs' ? 'Vybraný soubor:' : 'Selected file:'} {selectedFile.name}
                          </p>
                          {selectedFile.type.startsWith('image/') && (
                            <img
                              src={URL.createObjectURL(selectedFile)}
                              alt="Preview"
                              className="admin-mt-2 admin-h-32 admin-object-contain admin-rounded"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-mt-6 admin-flex admin-items-center">
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-mr-2"
                >
                  {editMode
                    ? language === 'cs'
                      ? 'Aktualizovat'
                      : 'Update'
                    : language === 'cs'
                    ? 'Přidat'
                    : 'Add'}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={resetForm}
                >
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">
          {language === 'cs' ? 'Seznam nastavení' : 'Settings List'}
        </div>
        {settings.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">
            {language === 'cs' ? 'Žádná nastavení nebyla nalezena.' : 'No settings found.'}
          </div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Klíč' : 'Key'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Skupina' : 'Group'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Popisek' : 'Label'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Hodnota' : 'Value'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {settings.map((setting) => {
                  const settingData = setting.translations && setting.translations[language]
                    ? setting.translations[language]
                    : (setting.translations && setting.translations.en
                      ? setting.translations.en
                      : { label: '', setting_value: '' });
                  
                  return (
                    <tr key={setting.id} className="admin-table-row">
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-font-medium admin-text-white">
                          {setting.setting_key || ''}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">
                          {setting.setting_group || 'general'}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-break-words">
                        <div className="admin-text-sm admin-text-white">
                          {settingData.label || ''}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-break-words">
                        <div className="admin-text-sm admin-text-white">
                          {settingData.setting_value || setting.value || ''}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                        <button 
                          onClick={() => handleEdit(setting.id)} 
                          className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3"
                        >
                          {language === 'cs' ? 'Upravit' : 'Edit'}
                        </button>
                        <button 
                          onClick={() => handleDelete(setting.id)} 
                          className="admin-text-red-600 hover:admin-text-red-900"
                        >
                          {language === 'cs' ? 'Smazat' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsEditor;